import { type FireAnalyticsEvent } from '../../../common/types';
import { EVENT_SOURCE, SEARCH_DIALOG_EVENT_SOURCE } from '../../constants/analytics';
import { type ConnectorSource } from '../../constants/schemas/3p-config';
import { type CommonAttributes, type NonPrivacySafeAttributes } from './types';

/**
 * This distinguishes the source of the OAuth event in search.
 * More information: https://hello.atlassian.net/wiki/x/VZcRSQE
 */
// oxlint-disable-next-line eslint/no-redeclare
export const OAuthSource = {
	quickFindFilter: 'quick-find-filter',
	quickFindOAuthBanner: 'quick-find-oauth-banner',
	quickFindBulkConnect: 'quick-find-bulk-connect',
	quickFindGithubTipBanner: 'quick-find-github-tip-banner',
	searchPageFilter: 'search-page-filter',
	searchPageOAuthBanner: 'search-page-oauth-banner',
	searchPageBulkConnect: 'search-page-bulk-connect',
	sainThirdPartyBanner: 'sain-third-party-banner',
} as const;

export type OAuthSource = (typeof OAuthSource)[keyof typeof OAuthSource];

interface OAuthEventProps {
	cloudId: string;
	product: string;
	connectorSource?: ConnectorSource;
	commonAttributes: CommonAttributes;
	nonPrivacySafeAttributes: NonPrivacySafeAttributes;
	isOnSearchDialog?: boolean;
	oAuthSource: OAuthSource;
	isImpersonated: boolean;
	extraAttributes?: Record<string, unknown>;
}

// https://data-portal.internal.atlassian.com/analytics/registry/71661
export const onOAuthStarted = ({
	cloudId,
	product,
	connectorSource,
	commonAttributes,
	nonPrivacySafeAttributes,
	isOnSearchDialog,
	oAuthSource,
	isImpersonated,
	extraAttributes,
}: OAuthEventProps): FireAnalyticsEvent => {
	return {
		source: isOnSearchDialog ? SEARCH_DIALOG_EVENT_SOURCE : EVENT_SOURCE,
		action: 'clicked',
		actionSubject: 'thirdPartyOAuth',
		attributes: {
			additionalAttributes: {
				...extraAttributes,
				cloudId,
				product,
				connectorSource,
				oAuthSource,
				isImpersonated,
			},
			commonAttributes,
		},
		nonPrivacySafeAttributes,
		eventType: 'track',
	};
};

// https://data-portal.internal.atlassian.com/analytics/registry/86359
export const onOAuthCompleted = ({
	cloudId,
	product,
	connectorSource,
	commonAttributes,
	nonPrivacySafeAttributes,
	isOnSearchDialog,
	oAuthSource,
	isImpersonated,
	extraAttributes,
}: OAuthEventProps): FireAnalyticsEvent => {
	return {
		source: isOnSearchDialog ? SEARCH_DIALOG_EVENT_SOURCE : EVENT_SOURCE,
		action: 'completed',
		actionSubject: 'thirdPartyOAuth',
		attributes: {
			additionalAttributes: {
				...extraAttributes,
				cloudId,
				product,
				connectorSource,
				oAuthSource,
				isImpersonated,
			},
			commonAttributes,
		},
		nonPrivacySafeAttributes,
		eventType: 'track',
	};
};

// https://data-portal.internal.atlassian.com/analytics/registry/86401
export const onOAuthFailed = ({
	cloudId,
	product,
	connectorSource,
	commonAttributes,
	nonPrivacySafeAttributes,
	isOnSearchDialog,
	oAuthSource,
	isImpersonated,
	extraAttributes,
}: OAuthEventProps): FireAnalyticsEvent => {
	return {
		source: isOnSearchDialog ? SEARCH_DIALOG_EVENT_SOURCE : EVENT_SOURCE,
		action: 'failed',
		actionSubject: 'thirdPartyOAuth',
		attributes: {
			additionalAttributes: {
				...extraAttributes,
				cloudId,
				product,
				connectorSource,
				oAuthSource,
				isImpersonated,
			},
			commonAttributes,
		},
		nonPrivacySafeAttributes,
		eventType: 'track',
	};
};

// Analytics event for when user hovers over the OAuth connect button
export const onOAuthConnectHovered = ({
	cloudId,
	product,
	connectorSource,
	commonAttributes,
	nonPrivacySafeAttributes,
	isOnSearchDialog,
	oAuthSource,
	isImpersonated,
	extraAttributes,
}: OAuthEventProps): FireAnalyticsEvent => {
	return {
		source: isOnSearchDialog ? SEARCH_DIALOG_EVENT_SOURCE : EVENT_SOURCE,
		action: 'hovered',
		actionSubject: 'thirdPartyOAuth',
		actionSubjectId: 'thirdPartyOAuthConnectButton',
		attributes: {
			additionalAttributes: {
				...extraAttributes,
				cloudId,
				product,
				connectorSource,
				oAuthSource,
				isImpersonated,
			},
			commonAttributes,
		},
		nonPrivacySafeAttributes,
		eventType: 'ui',
	};
};
